/**
 * /ambassadors — Public marketing page for the TruBeauté Ambassador
 * program. This is the link the founder pastes into her TikTok bio,
 * IG link tree, Linktree, etc. Anyone can land here, scan the offer,
 * and click through to /ambassadors/apply.
 *
 * Fully localised via `ambassadorsPublic.*` keys (en/es/fr/pt).
 * No auth required, no analytics gating — public landing.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  ArrowRight,
  Heart,
  Repeat,
  Wallet,
  Check,
} from "lucide-react";

const AmbassadorsPublic = () => {
  const { t } = useTranslation();

  return (
    <div
      className="min-h-screen bg-[#FDFBF7] text-[#2C2123]"
      data-testid="ambassadors-public-page"
    >
      {/* Header */}
      <header className="px-5 sm:px-10 pt-6 pb-3 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2.5"
          data-testid="ambassadors-public-brand"
        >
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <span className="font-serif italic text-xl tracking-tight">
            TruBeauté
          </span>
        </Link>
        <Link
          to="/"
          className="text-xs text-[#5C4B51] hover:text-[#2C2123] underline"
        >
          {t("ambassadorsPublic.navBack")}
        </Link>
      </header>

      {/* Hero */}
      <section className="px-5 sm:px-10 pt-10 sm:pt-16 pb-12 sm:pb-20 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-[#EADDD7] px-3.5 py-1.5 text-[11px] uppercase tracking-[0.22em] text-[#5D4050] mb-6">
          <Heart className="h-3 w-3" fill="currentColor" />
          {t("ambassadorsPublic.hero.eyebrow")}
        </div>
        <h1
          className="font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          data-testid="ambassadors-public-headline"
        >
          {t("ambassadorsPublic.hero.headline")}
        </h1>
        <p className="mt-5 text-base sm:text-lg text-[#5C4B51] leading-relaxed max-w-2xl">
          {t("ambassadorsPublic.hero.subline")}
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <Link
            to="/ambassadors/apply"
            className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white px-6 py-3 rounded-full hover:bg-[#805557] transition-colors font-medium"
            data-testid="ambassadors-public-cta-primary"
          >
            {t("ambassadorsPublic.hero.cta")}
            <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="#how-it-works"
            className="inline-flex items-center gap-1 text-sm text-[#5C4B51] hover:text-[#2C2123] underline-offset-4 hover:underline"
            data-testid="ambassadors-public-cta-secondary"
          >
            {t("ambassadorsPublic.hero.ctaSecondary")} ↓
          </a>
        </div>
      </section>

      {/* Stat tiles */}
      <section className="px-5 sm:px-10 pb-14 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stat
            icon={Wallet}
            label={t("ambassadorsPublic.stats.rate")}
            value={t("ambassadorsPublic.stats.rateValue")}
            note={t("ambassadorsPublic.stats.rateNote")}
            testid="ambassadors-stat-rate"
          />
          <Stat
            icon={Repeat}
            label={t("ambassadorsPublic.stats.recurring")}
            value={t("ambassadorsPublic.stats.recurringValue")}
            note={t("ambassadorsPublic.stats.recurringNote")}
            testid="ambassadors-stat-recurring"
          />
          <Stat
            icon={Check}
            label={t("ambassadorsPublic.stats.payout")}
            value={t("ambassadorsPublic.stats.payoutValue")}
            note={t("ambassadorsPublic.stats.payoutNote")}
            testid="ambassadors-stat-payout"
          />
        </div>
      </section>

      {/* How it works */}
      <section
        id="how-it-works"
        className="px-5 sm:px-10 py-14 bg-[#FBF4EF] border-y border-[#EADDD7]"
        data-testid="ambassadors-how"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif italic text-3xl sm:text-4xl mb-8">
            {t("ambassadorsPublic.how.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Step
              title={t("ambassadorsPublic.how.step1Title")}
              body={t("ambassadorsPublic.how.step1Body")}
              testid="ambassadors-step-1"
            />
            <Step
              title={t("ambassadorsPublic.how.step2Title")}
              body={t("ambassadorsPublic.how.step2Body")}
              testid="ambassadors-step-2"
            />
            <Step
              title={t("ambassadorsPublic.how.step3Title")}
              body={t("ambassadorsPublic.how.step3Body")}
              testid="ambassadors-step-3"
            />
          </div>
        </div>
      </section>

      {/* Why ambassadors love it */}
      <section className="px-5 sm:px-10 py-14 max-w-3xl mx-auto">
        <h2 className="font-serif italic text-3xl sm:text-4xl mb-5">
          {t("ambassadorsPublic.why.title")}
        </h2>
        <p className="text-base text-[#5C4B51] leading-relaxed mb-4">
          {t("ambassadorsPublic.why.p1")}
        </p>
        <p className="text-base text-[#5C4B51] leading-relaxed">
          {t("ambassadorsPublic.why.p2")}
        </p>
      </section>

      {/* FAQ */}
      <section
        className="px-5 sm:px-10 py-14 bg-[#FBF4EF] border-y border-[#EADDD7]"
        data-testid="ambassadors-faq"
      >
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif italic text-3xl sm:text-4xl mb-6">
            {t("ambassadorsPublic.faq.title")}
          </h2>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((n) => (
              <details
                key={n}
                className="bg-white border border-[#EADDD7] rounded-2xl p-4"
                data-testid={`ambassadors-faq-${n}`}
              >
                <summary className="cursor-pointer font-medium text-[#2C2123] flex items-center justify-between">
                  {t(`ambassadorsPublic.faq.q${n}`)}
                  <span className="text-[#9B6A6C] text-xs">+</span>
                </summary>
                <p className="text-sm text-[#5C4B51] mt-3 leading-relaxed">
                  {t(`ambassadorsPublic.faq.a${n}`)}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="px-5 sm:px-10 py-16 sm:py-20 max-w-2xl mx-auto text-center">
        <h2 className="font-serif italic text-3xl sm:text-4xl mb-3">
          {t("ambassadorsPublic.footerCta.title")}
        </h2>
        <p className="text-base text-[#5C4B51] mb-6">
          {t("ambassadorsPublic.footerCta.body")}
        </p>
        <Link
          to="/ambassadors/apply"
          className="inline-flex items-center gap-2 bg-[#9B6A6C] text-white px-7 py-3.5 rounded-full hover:bg-[#805557] transition-colors text-base font-medium"
          data-testid="ambassadors-public-cta-footer"
        >
          {t("ambassadorsPublic.footerCta.cta")}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, note, testid }) => (
  <div
    className="bg-white border border-[#EADDD7] rounded-2xl p-5"
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#8A7D82] mb-2">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div className="font-serif italic text-3xl text-[#2C2123]">{value}</div>
    <div className="text-xs text-[#8A7D82] mt-1.5">{note}</div>
  </div>
);

const Step = ({ title, body, testid }) => (
  <div
    className="bg-white border border-[#EADDD7] rounded-2xl p-5"
    data-testid={testid}
  >
    <h3 className="font-serif italic text-xl text-[#2C2123] mb-2">{title}</h3>
    <p className="text-sm text-[#5C4B51] leading-relaxed">{body}</p>
  </div>
);

export default AmbassadorsPublic;
